// === 크레딧 가격·지급량 (2.3.0) ======================================
// 서버 functions/credit-config.js 와 같은 Firestore `config/credits` 문서를
// 읽어 화면에 같은 숫자를 보여준다. 실제 차감은 언제나 서버가 하므로 여기
// 값이 틀려도 손해는 "표시가 어긋남" 뿐이다 — 그래도 아래 DEFAULTS 는 서버
// DEFAULTS 와 같게 둘 것 (문서를 못 읽은 첫 렌더가 이 값이다).
//
// 단위: 생성 1장 = price.generate (10). 게스트 잔액만 예외로 "디자인 개수" 로
// localStorage 에 있다 (구버전과 같은 키·단위) — credits-service.js 참고.
//
// 바꾸는 법: Firebase 콘솔에서 config/credits 수정 → 서버는 5분, 앱은 아래
// TTL 안에 반영. 앱 빌드·심사 불필요.

import { useEffect, useState } from 'react';
import { db } from '../firebase.js';
import { doc, getDoc } from 'firebase/firestore';

export const CREDIT_DEFAULTS = Object.freeze({
  daily: 20,
  signupBonus: 30,
  inviteReward: 50,
  inviteCap: 10,
  guestDesigns: 2,
  // Gio 대화는 무료 (2026-10-28). 하루 상한 chatDailyCap 은 서버만 안다.
  price: Object.freeze({ generate: 10, edit: 10, chat: 0, chatPhoto: 0 }),
  // 스토어 상품 ID → 지급 크레딧. 가격은 스토어가 원본 (RevenueCat priceString).
  packs: Object.freeze({ archelier_credits_200: 200, archelier_credits_600: 600 }),
});

const CHANGED = 'voda:creditConfigChanged';
const TTL = 30 * 60 * 1000;
let cache = { at: 0, val: CREDIT_DEFAULTS };
let inflight = null;

const posInt = (v, fallback) => (Number.isInteger(v) && v >= 0 ? v : fallback);

function normalizePacks(raw) {
  if (!raw || typeof raw !== 'object') return { ...CREDIT_DEFAULTS.packs };
  const out = {};
  for (const [id, n] of Object.entries(raw)) {
    if (/^[a-z0-9_.]{1,100}$/.test(id) && Number.isInteger(n) && n > 0 && n <= 100000) out[id] = n;
  }
  return out;
}

// 서버 normalize() 와 같은 규칙 — 깨진 값은 기본값. 생성·편집 가격은 0 불가, 대화는 0 = 무료.
function normalize(d) {
  const p = (d && typeof d.price === 'object' && d.price) || {};
  const D = CREDIT_DEFAULTS;
  return {
    daily: posInt(d.daily, D.daily),
    signupBonus: posInt(d.signupBonus, D.signupBonus),
    inviteReward: posInt(d.inviteReward, D.inviteReward),
    inviteCap: posInt(d.inviteCap, D.inviteCap),
    guestDesigns: posInt(d.guestDesigns, D.guestDesigns),
    price: {
      generate: posInt(p.generate, D.price.generate) || D.price.generate,
      edit: posInt(p.edit, D.price.edit) || D.price.edit,
      chat: posInt(p.chat, D.price.chat),
      chatPhoto: posInt(p.chatPhoto, D.price.chatPhoto),
    },
    packs: normalizePacks(d.packs),
  };
}

/** 동기 조회 — 아직 못 받았으면 기본값. */
export function getCreditConfig() {
  return cache.val;
}

export async function loadCreditConfig() {
  if (cache.at && Date.now() - cache.at < TTL) return cache.val;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const snap = await getDoc(doc(db, 'config', 'credits'));
      const val = normalize(snap.exists() ? snap.data() : {});
      const changed = JSON.stringify(val) !== JSON.stringify(cache.val);
      cache = { at: Date.now(), val };
      if (changed) window.dispatchEvent(new CustomEvent(CHANGED));
    } catch (e) {
      // 못 읽어도 화면이 멈추면 안 된다 — 기본값 유지, 다음 TTL 에 재시도.
      console.warn('credit config load failed:', e?.message);
      cache = { at: Date.now(), val: cache.val };
    } finally {
      inflight = null;
    }
    return cache.val;
  })();
  return inflight;
}

/** React hook — 값이 바뀌면 다시 렌더. */
export function useCreditConfig() {
  const [val, setVal] = useState(cache.val);
  useEffect(() => {
    loadCreditConfig().then(setVal);
    const onChange = () => setVal(cache.val);
    window.addEventListener(CHANGED, onChange);
    return () => window.removeEventListener(CHANGED, onChange);
  }, []);
  return val;
}
